import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userLogin } from '../api/api';

// Types
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  address1: string | null;
  address2: string | null;
  country: string | null;
  city: string | null;
  role: string;
  magicLinkToken: string | null;
  magicLinkExpiresAt: string | null;
  otp: string | null;
  otpExpiresAt: string | null;
  customerId: string | null;
  isVerified: boolean;
  isMagicLink: boolean;
  donationCount: number;
  totalDonated: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

// Helper function to get full name
export const getUserFullName = (user: User | null): string => {
  if (!user) return '';
  return `${user.firstName} ${user.lastName}`.trim();
};

export interface AuthState {
  user: User | null;
  token: string | null;
  navigation: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  justLoggedIn: boolean;
}

// Initial state - Redux Persist will handle restoration
const initialState: AuthState = {
  user: null,
  token: null,
  navigation: [],
  isAuthenticated: false,
  isLoading: false,
  error: null,
  justLoggedIn: false,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await userLogin(credentials);
      const data = response.data;
      
      return {
        user: data.user,
        token: data.token,
        navigation: data.navigation || [],
      };
    } catch (error: any) {
      // Handle API errors
      
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed';
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async () => {
    // Call logout API if needed
    // await fetch('/api/auth/logout', { method: 'POST' });
    
    return null;
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.navigation = [];
      state.isAuthenticated = false;
      state.error = null;
      state.justLoggedIn = false;
    },
    clearJustLoggedIn: (state) => {
      state.justLoggedIn = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.navigation = action.payload.navigation;
        state.isAuthenticated = true;
        state.error = null;
        state.justLoggedIn = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.navigation = [];
        state.isAuthenticated = false;
        state.error = action.payload as string;
        state.justLoggedIn = false;
      })
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.navigation = [];
        state.isAuthenticated = false;
        state.error = null;
        state.justLoggedIn = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
  },
});

export const { clearError, setUser, clearAuth, clearJustLoggedIn } = authSlice.actions;
export default authSlice.reducer;
